import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { toast } from 'sonner';
import type { ApiResponse } from '@/types/api';
import { getAuthToken, logout } from '@/stores/userStore';
import { cancelManager } from '.';
import type { IRequestConfig } from '.';
import { APP_CONFIG } from '@/config/env';
import { addMsgOnReloaded } from '@/utils/msgOnReload';
import { ApiBusinessError, parseUserError } from '@/utils/userError';

/** 统一生成用户文案，保留原始异常供调用方诊断及识别取消 */
const parseRequestError = (error: unknown, config?: IRequestConfig) =>
  parseUserError(error, {
    context: config?.errorContext,
    online: typeof navigator === 'undefined' ? undefined : navigator.onLine,
  });

/** 未迁移的请求默认保留 Toast；局部提示由页面单独接管 */
const notifyRequestError = (message: string | undefined, config?: IRequestConfig) => {
  if (message && config?.showErrorToast !== false) {
    toast.error(message);
  }
};

/** 无配置的运行异常交由请求入口提示，才能保留页面传入的提示责任及操作上下文 */
export const notifyUnconfiguredRequestError = (error: unknown, config?: IRequestConfig) => {
  if (
    error instanceof ApiBusinessError ||
    (typeof error === 'object' && error !== null && 'config' in error && error.config)
  )
    return;
  const parsed = parseRequestError(error, config);
  if (parsed.kind === 'canceled') return;
  notifyRequestError(parsed.message, config);
  console.error('请求执行失败:', error);
};

/**
 * 请求拦截器
 */
export const setupRequestInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getAuthToken();
      // 如果存在 token,添加到请求头
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error: AxiosError) => {
      console.error('请求错误:', error);
      return Promise.reject(error);
    }
  );
};

/**
 * 响应拦截器
 */
export const setupResponseInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      cancelManager.removeRequest(response.config);
      if (APP_CONFIG.isDev) {
        console.log(`[cancelManager]移除已完成的请求：${response.config.headers?.['Request-Key']}`);
      }

      // 如果响应是二进制流（Blob/ArrayBuffer），直接返回，因为导出只会回传文件流
      if (response.data instanceof Blob || response.data instanceof ArrayBuffer) {
        return response;
      }

      const { code, msg } = response.data;
      if (code === 401 || code === 403) {
        // 清除用户信息（用于退出登录）
        logout();
        addMsgOnReloaded({ type: 'error', content: msg || '未授权,请重新登录' });
        window.location.hash = '/login';
        window.location.reload();
        return Promise.reject(
          new ApiBusinessError({ code, msg }, msg || '未授权,请重新登录', { cause: response })
        );
      }
      // 业务成功
      if (code === 0) {
        return response;
      }
      // 业务错误
      const parsed = parseRequestError(response.data, response.config);
      const message = parsed.message || '请求失败，请稍后重试';
      notifyRequestError(message, response.config);
      return Promise.reject(new ApiBusinessError({ code, msg }, message, { cause: response }));
    },
    (error: AxiosError<ApiResponse>) => {
      if (error.config) {
        cancelManager.removeRequest(error.config);
        if (APP_CONFIG.isDev) {
          console.log(
            `[cancelManager]移除已${error.code === 'ERR_CANCELED' ? '取消' : '失败'}的请求：${error.config.headers?.['Request-Key']}`
          );
        }
      }
      // 某些运行异常没有 Axios 配置，在请求入口使用原配置处理，避免误弹全局提示
      if (!error.config) return Promise.reject(error);
      const parsed = parseRequestError(error, error.config);
      if (parsed.kind === 'canceled') return Promise.reject(error);
      if (error.response?.status === 401) {
        // 认证通知独立于局部提示开关，沿用原会话清理行为
        toast.error('未授权,请重新登录');
        logout();
      } else {
        notifyRequestError(parsed.message, error.config);
      }
      console.error('响应错误:', error);

      return Promise.reject(error);
    }
  );
};

/**
 * 设置所有拦截器
 */
export const setupInterceptors = (instance: AxiosInstance) => {
  setupRequestInterceptor(instance);
  setupResponseInterceptor(instance);
};
